// Cook mode: one big step at a time, screen kept awake, step ingredients with amounts,
// one-tap timers (several at once), big Previous/Next buttons, progress remembered.
// Last page: "I cooked it" → meal photo → counts for streak and path.
// Hands-free: the microphone button listens for a few spoken commands (logic/voiceCommands.ts) and reads
// steps aloud. Recognition runs on the device where the browser can; otherwise it asks before using the
// browser's own speech service. Nothing goes to Chefi's server.
import React, { useEffect, useRef, useState } from 'react';
import { Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { RootParams } from '../navigation';
import { useAllIngredients, useLibrary, useRecipe } from '../store/library';
import { useProgress } from '../store/progress';
import { useSettings } from '../store/settings';
import { useTimers } from '../store/timers';
import { activateKeepAwake, deactivateKeepAwake } from '../platform/keepAwake';
import { scaleIngredient } from '../logic/scaling';
import { formatQuantity, ingredientLine } from '../logic/format';
import { resolveUnit, toSystem } from '../logic/units';
import { detectTimers, formatClock } from '../logic/timerText';
import { answerHowMuch, parseVoiceCommand, type VoiceCommand } from '../logic/voiceCommands';
import { listen, recognitionMode, speak, speakSupported, speechSupported, stopSpeaking, type Listener } from '../platform/speech';
import { confirm, notify } from '../components/Dialogs';
import i18n from '../i18n';
import Photo from '../components/Photo';
import { TimerList } from '../components/TimerBar';
import { Body, Button, Caption, Chip, IconButton, Row, Screen } from '../components/ui';
import { font, spacing, usePalette } from '../theme';

const SCALES = [1, 1.25, 1.5] as const;

export default function CookModeScreen({ route, navigation }: NativeStackScreenProps<RootParams, 'CookMode'>) {
  const { t } = useTranslation();
  const p = usePalette();
  const insets = useSafeAreaInsets();
  const { recipe } = useRecipe(route.params.recipeId);
  const ingredients = useAllIngredients();
  const units = useLibrary(s => s.units);
  const { cookStep, setCookStep } = useProgress();
  const { cookFontScale, convertUnits, unitSystem, set } = useSettings();
  const start = useTimers(s => s.start);
  const cancelTimer = useTimers(s => s.cancel);
  const timers = useTimers(s => s.timers);
  const [listening, setListening] = useState(false);
  const [heard, setHeard] = useState('');
  const listener = useRef<Listener | null>(null);
  const serviceAllowed = useRef(false);
  const onCommand = useRef<(c: VoiceCommand) => void>(() => {});
  const [now, setNow] = useState(Date.now());
  const [index, setIndex] = useState(() => Math.min(cookStep[route.params.recipeId] ?? 0, recipe?.steps.length ?? 0));

  useEffect(() => {
    activateKeepAwake();
    return () => deactivateKeepAwake();
  }, []);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => setCookStep(route.params.recipeId, index), [index, route.params.recipeId, setCookStep]);
  useEffect(() => () => {
    listener.current?.stop();
    stopSpeaking();
  }, []);
  // Hands-free: every new step is read aloud while listening.
  const readStep = useRef<() => void>(() => {});
  useEffect(() => {
    if (listening) readStep.current();
  }, [index, listening]);

  if (!recipe) return null;
  const total = recipe.steps.length;
  const onFinalPage = index >= total;
  const step = recipe.steps[index];
  const cookFor = route.params.cookFor ?? recipe.servings;
  const s = cookFontScale;

  const amountText = (l: (typeof recipe.ingredients)[number]) => {
    let value = scaleIngredient(l.amount, recipe.servings, cookFor);
    let unit = resolveUnit(l.unit, units);
    if (convertUnits && unit) ({ value, unit } = toSystem(value, unit, unitSystem));
    return ingredientLine(formatQuantity(value, unit), unit?.symbol ?? null, ingredients[l.ingredientId]?.title ?? l.ingredientId, t('common.of'));
  };
  const stepTimers = step ? [...step.timers, ...detectTimers(`${step.title} ${step.explainer}`).filter(d => !step.timers.some(x => x.seconds === d.seconds))] : [];
  const mine = timers.filter(x => x.recipeId === recipe.id);

  const lang = i18n.language.includes('-') ? i18n.language : i18n.language === 'de' ? 'de-DE' : 'en-US';
  const say = (text: string) => speak(text, lang);
  readStep.current = () => say(step ? [t('cook.stepOf', { n: index + 1, total }), step.title, step.explainer].filter(Boolean).join('. ') : t('cook.finishedTitle'));
  onCommand.current = c => {
    switch (c.kind) {
      case 'next': return setIndex(i => Math.min(total, i + 1));
      case 'previous': return setIndex(i => Math.max(0, i - 1));
      case 'goto': return setIndex(Math.max(0, Math.min(total - 1, c.step < 0 ? total - 1 : c.step - 1)));
      case 'repeat': return readStep.current();
      case 'ingredients': {
        const lines = step?.ingredients.length ? step.ingredients : recipe.ingredients;
        return say(lines.map(amountText).join(', ') || t('cook.voiceNoIngredients'));
      }
      case 'howMuch': {
        const lines = [...(step?.ingredients ?? []), ...recipe.ingredients].map(l => ({ title: ingredients[l.ingredientId]?.title ?? l.ingredientId, text: amountText(l) }));
        const hit = answerHowMuch(c.query, lines);
        return say(hit ? hit.text : t('cook.voiceNotFound', { name: c.query }));
      }
      case 'timer':
        start(step?.title.slice(0, 30) || recipe.title, recipe.id, step?.id ?? '', c.seconds);
        return say(t('cook.voiceTimer', { time: formatClock(c.seconds) }));
      case 'stopTimers':
        mine.forEach(x => cancelTimer(x.id));
        return say(t('cook.voiceTimersStopped'));
      case 'stopListening':
        return stopListening();
    }
  };
  const stopListening = () => {
    listener.current?.stop();
    listener.current = null;
    setListening(false);
    setHeard('');
  };
  const toggleListening = async () => {
    if (listening) return stopListening();
    const mode = await recognitionMode(lang);
    if (mode === 'none') return notify(t('cook.voiceUnsupported'));
    if (mode === 'service' && !serviceAllowed.current) {
      if (!(await confirm(t('cook.voiceServiceTitle'), t('cook.voiceServiceBody'), t('cook.voiceAllow'), false))) return;
      serviceAllowed.current = true;
    }
    setListening(true);
    listener.current = listen({
      lang, mode,
      onPhrase: text => {
        const command = parseVoiceCommand(text);
        setHeard(command ? text : t('cook.voiceUnknown', { text }));
        if (command) onCommand.current(command);
      },
      onError: code => {
        setListening(false);
        listener.current = null;
        notify(code === 'not-allowed' || code === 'service-not-allowed' ? t('cook.voiceDenied') : t('cook.voiceUnsupported'));
      },
      onEnd: () => setListening(false),
    });
  };

  return (
    <View style={{ flex: 1, backgroundColor: p.background, paddingTop: insets.top }}>
      <Row style={{ paddingHorizontal: spacing.l, paddingVertical: spacing.s, flexWrap: 'nowrap' }} gap={spacing.m}>
        <IconButton icon="close" label={t('common.close')} onPress={() => navigation.goBack()} size={28} />
        <Text style={{ flex: 1, fontSize: font.headline, fontWeight: '700', color: p.text }} numberOfLines={1}>{recipe.title || t('cook.title')}</Text>
        {speechSupported ? <IconButton icon={listening ? 'mic' : 'micOff'} label={listening ? t('cook.voiceStop') : t('cook.voiceStart')} color={listening ? p.tint : undefined} onPress={toggleListening} size={26} /> : null}
        {speakSupported && !onFinalPage ? <IconButton icon="speak" label={t('cook.readAloud')} onPress={() => readStep.current()} size={26} /> : null}
        <IconButton icon="textSize" label="Aa" onPress={() => set({ cookFontScale: SCALES[(SCALES.indexOf(s) + 1) % SCALES.length] })} size={26} />
      </Row>

      <Screen contentStyle={{ gap: spacing.xl }}>
        {!onFinalPage && step ? (
          <>
            <Caption>{t('cook.stepOf', { n: index + 1, total })}</Caption>
            {step.image ? <Photo media={step.image} seed={step.id} style={{ width: '100%', aspectRatio: 16 / 9 }} /> : null}
            <Text style={{ fontSize: 28 * s, lineHeight: 36 * s, fontWeight: '800', color: p.text }}>{step.title}</Text>
            {step.explainer ? <Text style={{ fontSize: 20 * s, lineHeight: 30 * s, color: p.text }}>{step.explainer}</Text> : !step.title ? <Body secondary>{t('cook.noText')}</Body> : null}
            {step.ingredients.length || step.utensils.length ? (
              <View style={{ gap: spacing.s }}>
                <Caption>{t('cook.ingredients')}</Caption>
                {step.ingredients.map(l => (
                  <Text key={l.ingredientId} style={{ fontSize: 20 * s, color: p.text }}>• {amountText(l)}</Text>
                ))}
              </View>
            ) : null}
            {stepTimers.length ? (
              <Row gap={spacing.m}>
                {stepTimers.map((tm, i) => (
                  <Button key={i} icon="timer" label={t('cook.startTimer', { time: formatClock(tm.seconds) })} variant="secondary" big onPress={() => start(tm.label || step.title.slice(0, 30), recipe.id, step.id, tm.seconds)} />
                ))}
              </Row>
            ) : null}
          </>
        ) : (
          <View style={{ gap: spacing.l, alignItems: 'center', paddingVertical: spacing.xxl }}>
            <Text style={{ fontSize: 30 * s, fontWeight: '800', color: p.text, textAlign: 'center' }}>{t('cook.finishedTitle')}</Text>
            <Body secondary style={{ textAlign: 'center', fontSize: 18 * s }}>{t('cook.finishedBody')}</Body>
            <Button
              label={t('logMeal.cookedIt')}
              icon="camera"
              big
              onPress={() => navigation.replace('LogMeal', { recipeId: recipe.id })}
              style={{ alignSelf: 'stretch' }}
            />
            <Button
              label={t('cook.finish')}
              variant="secondary"
              onPress={() => {
                setCookStep(recipe.id, null);
                navigation.goBack();
              }}
              style={{ alignSelf: 'stretch' }}
            />
          </View>
        )}
        {mine.length ? <TimerList now={now} /> : null}
        <Row style={{ flexWrap: 'nowrap' }}>
          <Chip icon="unit" label={t('recipe.convertUnits')} selected={convertUnits} onPress={() => set({ convertUnits: !convertUnits })} iconOnly />
        </Row>
        {listening ? <Caption>{heard ? t('cook.voiceHeard', { text: heard }) : t('cook.voiceHelp')}</Caption> : null}
        <Caption>{t('cook.keepAwake')}</Caption>
      </Screen>

      <Row style={{ flexWrap: 'nowrap', paddingHorizontal: spacing.l, paddingTop: spacing.s, paddingBottom: insets.bottom + spacing.m, borderTopWidth: 0.5, borderTopColor: p.border }} gap={spacing.m}>
        <Button label={t('cook.previous')} icon="back" variant="secondary" big disabled={index === 0} onPress={() => setIndex(i => Math.max(0, i - 1))} style={{ flex: 1 }} iconOnly />
        <Button label={onFinalPage ? t('cook.finish') : t('cook.next')} icon="forward" big disabled={onFinalPage} onPress={() => setIndex(i => Math.min(total, i + 1))} style={{ flex: 1 }} iconOnly />
      </Row>
    </View>
  );
}
